@file:OptIn(ExperimentalJsExport::class)
@file:JsExport

package game

import kotlin.js.ExperimentalJsExport

// 1. 导出枚举类型
@JsExport
enum class ActionType {
    TICK,
    START_GAME,
    RESTART,
    TO_MENU,
    OPEN_GARAGE,
    OPEN_SHOP,
    PAUSE,
    RESUME,
    SELECT_CHASSIS,
    BUY_WEAPON,
    BUY_CHASSIS,
    ADD_COINS,
    GRANT_WEAPON,
    GRANT_CHASSIS,
    SET_GOD_MODE,
    GRANT_SKILL,
    GRANT_POWERUP,
    TOGGLE_DAMAGE_NUMBERS,
    TOGGLE_DEBUG_PANEL
}

// 2. 导出接口
@JsExport
interface IGameAction {
    val type: ActionType
}

// 3. sealed class 不加 @JsExport
sealed class GameAction : IGameAction {
    // 在 data class 中，type 作为构造函数参数
    data class Tick(
        val input: PlayerInput,
        val dt: Double
    ) : GameAction() {
        override val type = ActionType.TICK
    }
    
    object StartGame : GameAction() {
        override val type = ActionType.START_GAME
    }
    
    object Restart : GameAction() {
        override val type = ActionType.RESTART
    }
    
    object ToMenu : GameAction() {
        override val type = ActionType.TO_MENU
    }
    
    object OpenGarage : GameAction() {
        override val type = ActionType.OPEN_GARAGE
    }
    
    object OpenShop : GameAction() {
        override val type = ActionType.OPEN_SHOP
    }
    
    object Pause : GameAction() {
        override val type = ActionType.PAUSE
    }
    
    object Resume : GameAction() {
        override val type = ActionType.RESUME
    }
    
    data class SelectChassis(
        val chassis: ChassisType
    ) : GameAction() {
        override val type = ActionType.SELECT_CHASSIS
    }
    
    data class BuyWeapon(
        val weapon: BulletType
    ) : GameAction() {
        override val type = ActionType.BUY_WEAPON
    }
    
    data class BuyChassis(
        val chassis: ChassisType
    ) : GameAction() {
        override val type = ActionType.BUY_CHASSIS
    }
    
    data class AddCoins(
        val amount: Int
    ) : GameAction() {
        override val type = ActionType.ADD_COINS
    }
    
    data class GrantWeapon(
        val weapon: BulletType
    ) : GameAction() {
        override val type = ActionType.GRANT_WEAPON
    }
    
    data class GrantChassis(
        val chassis: ChassisType
    ) : GameAction() {
        override val type = ActionType.GRANT_CHASSIS
    }
    
    data class SetGodMode(
        val on: Boolean
    ) : GameAction() {
        override val type = ActionType.SET_GOD_MODE
    }
    
    data class GrantSkill(
        val skill: SkillType
    ) : GameAction() {
        override val type = ActionType.GRANT_SKILL
    }
    
    data class GrantPowerup(
        val powerup: PowerupType
    ) : GameAction() {
        override val type = ActionType.GRANT_POWERUP
    }
    
    data class ToggleDamageNumbers(
        val on: Boolean
    ) : GameAction() {
        override val type = ActionType.TOGGLE_DAMAGE_NUMBERS
    }
    
    object ToggleDebugPanel : GameAction() {
        override val type = ActionType.TOGGLE_DEBUG_PANEL
    }
}

// 4. 工厂对象
@JsExport
object GameActionFactory {
    fun tick(input: PlayerInput, dt: Double): GameAction.Tick {
        return GameAction.Tick(input = input, dt = dt)
    }
    
    fun startGame(): GameAction.StartGame = GameAction.StartGame
    fun restart(): GameAction.Restart = GameAction.Restart
    fun toMenu(): GameAction.ToMenu = GameAction.ToMenu
    fun openGarage(): GameAction.OpenGarage = GameAction.OpenGarage
    fun openShop(): GameAction.OpenShop = GameAction.OpenShop
    fun pause(): GameAction.Pause = GameAction.Pause
    fun resume(): GameAction.Resume = GameAction.Resume
    
    fun selectChassis(chassis: ChassisType): GameAction.SelectChassis {
        return GameAction.SelectChassis(chassis = chassis)
    }
    
    fun buyWeapon(weapon: BulletType): GameAction.BuyWeapon {
        return GameAction.BuyWeapon(weapon = weapon)
    }
    
    fun buyChassis(chassis: ChassisType): GameAction.BuyChassis {
        return GameAction.BuyChassis(chassis = chassis)
    }
    
    fun addCoins(amount: Int): GameAction.AddCoins {
        return GameAction.AddCoins(amount = amount)
    }
    
    fun grantWeapon(weapon: BulletType): GameAction.GrantWeapon {
        return GameAction.GrantWeapon(weapon = weapon)
    }
    
    fun grantChassis(chassis: ChassisType): GameAction.GrantChassis {
        return GameAction.GrantChassis(chassis = chassis)
    }
    
    fun setGodMode(on: Boolean): GameAction.SetGodMode {
        return GameAction.SetGodMode(on = on)
    }
    
    fun grantSkill(skill: SkillType): GameAction.GrantSkill {
        return GameAction.GrantSkill(skill = skill)
    }
    
    fun grantPowerup(powerup: PowerupType): GameAction.GrantPowerup {
        return GameAction.GrantPowerup(powerup = powerup)
    }
    
    fun toggleDamageNumbers(on: Boolean): GameAction.ToggleDamageNumbers {
        return GameAction.ToggleDamageNumbers(on = on)
    }
    
    fun toggleDebugPanel(): GameAction.ToggleDebugPanel = GameAction.ToggleDebugPanel
}

// 5. 类型守卫工具
@JsExport
object GameActionUtils {
    fun isTick(action: IGameAction): Boolean = action.type == ActionType.TICK
    fun isStartGame(action: IGameAction): Boolean = action.type == ActionType.START_GAME
    fun isRestart(action: IGameAction): Boolean = action.type == ActionType.RESTART
    fun isToMenu(action: IGameAction): Boolean = action.type == ActionType.TO_MENU
    fun isOpenGarage(action: IGameAction): Boolean = action.type == ActionType.OPEN_GARAGE
    fun isOpenShop(action: IGameAction): Boolean = action.type == ActionType.OPEN_SHOP
    fun isPause(action: IGameAction): Boolean = action.type == ActionType.PAUSE
    fun isResume(action: IGameAction): Boolean = action.type == ActionType.RESUME
    fun isSelectChassis(action: IGameAction): Boolean = action.type == ActionType.SELECT_CHASSIS
    fun isBuyWeapon(action: IGameAction): Boolean = action.type == ActionType.BUY_WEAPON
    fun isBuyChassis(action: IGameAction): Boolean = action.type == ActionType.BUY_CHASSIS
    fun isAddCoins(action: IGameAction): Boolean = action.type == ActionType.ADD_COINS
    fun isGrantWeapon(action: IGameAction): Boolean = action.type == ActionType.GRANT_WEAPON
    fun isGrantChassis(action: IGameAction): Boolean = action.type == ActionType.GRANT_CHASSIS
    fun isSetGodMode(action: IGameAction): Boolean = action.type == ActionType.SET_GOD_MODE
    fun isGrantSkill(action: IGameAction): Boolean = action.type == ActionType.GRANT_SKILL
    fun isGrantPowerup(action: IGameAction): Boolean = action.type == ActionType.GRANT_POWERUP
    fun isToggleDamageNumbers(action: IGameAction): Boolean = action.type == ActionType.TOGGLE_DAMAGE_NUMBERS
    fun isToggleDebugPanel(action: IGameAction): Boolean = action.type == ActionType.TOGGLE_DEBUG_PANEL
}